import type React from 'react'
import type { JSX, Ref } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { Animations } from 'blocks/AnimatedWrapper'
import type { TabBorder, TabPadding } from './Tab.h'

import { TextBox, TextBoxColors, TextBoxTypes } from 'atoms/TextBox'
import { AnimatedWrapper } from 'blocks/AnimatedWrapper'
import { isGradient } from 'utils/helpers'
import { COLOR } from 'vars/color'
import { CORNERS } from 'vars/corners'
import { TabBackground } from './Tab.h'
import { TabGradientBorderSC, TabSimpleBorderSC } from './Tab.styled'

export interface ITabProps {
  ref?: Ref<HTMLDivElement>
  title: string
  disabled?: boolean
  selected?: boolean
  onClick?: (event?: React.MouseEvent<HTMLDivElement>) => void
  font?: ITextBoxProps['font']
  color?: ITextBoxProps['color']
  colorHovered?: ITextBoxProps['color']
  colorPressed?: ITextBoxProps['color']
  colorDisabled?: ITextBoxProps['color']
  colorSelected?: ITextBoxProps['color']
  colorSelectedHovered?: ITextBoxProps['color']
  colorSelectedPressed?: ITextBoxProps['color']
  colorSelectedDisabled?: ITextBoxProps['color']
  gradient?: TabBackground
  gradientSelected?: TabBackground
  background?: TabBackground
  backgroundHovered?: TabBackground
  backgroundPressed?: TabBackground
  backgroundSelected?: TabBackground
  backgroundDisabled?: TabBackground
  backgroundSelectedHovered?: TabBackground
  backgroundSelectedPressed?: TabBackground
  backgroundSelectedDisabled?: TabBackground
  borders?: TabBorder
  borderColor?: COLOR
  borderColorHovered?: COLOR
  borderColorPressed?: COLOR
  borderColorSelected?: COLOR
  borderColorDisabled?: COLOR
  borderColorSelectedDisabled?: COLOR
  paddings?: TabPadding
  transitionName?: Animations
  corners?: CORNERS
}

Tab.displayName = 'Tab'
export function Tab({
  ref,
  title = '',
  disabled,
  selected,
  onClick,
  color = TextBoxColors.SURFACE_DARK_7,
  colorHovered = TextBoxColors.SURFACE_LIGHT_2,
  colorPressed = TextBoxColors.BASE_MAIN_PRESSED,
  colorDisabled = TextBoxColors.TEXT_ICON_TERTIARY,
  colorSelected = TextBoxColors.TEXT_ICON_CONSTANT_LIGHT,
  colorSelectedHovered = TextBoxColors.TEXT_ICON_CONSTANT_LIGHT,
  colorSelectedPressed = TextBoxColors.TEXT_ICON_CONSTANT_LIGHT,
  colorSelectedDisabled = TextBoxColors.TEXT_ICON_TERTIARY,
  font = TextBoxTypes.Button1,
  gradient,
  gradientSelected,
  background = TabBackground.COLOR_TRANSPARENT,
  backgroundHovered = TabBackground.COLOR_TRANSPARENT,
  backgroundPressed = TabBackground.COLOR_TRANSPARENT,
  backgroundSelected = TabBackground.COLOR_TRANSPARENT,
  backgroundDisabled = TabBackground.COLOR_TRANSPARENT,
  backgroundSelectedHovered = TabBackground.COLOR_TRANSPARENT,
  backgroundSelectedPressed = TabBackground.COLOR_TRANSPARENT,
  backgroundSelectedDisabled = TabBackground.COLOR_TRANSPARENT,
  borders,
  borderColor = COLOR.TRANSPARENT,
  borderColorHovered = COLOR.TRANSPARENT,
  borderColorPressed = COLOR.TRANSPARENT,
  borderColorDisabled = COLOR.TRANSPARENT,
  borderColorSelected = COLOR.ACCENT_TERTIARY,
  borderColorSelectedDisabled = COLOR.DISABLED,
  paddings,
  transitionName,
  corners = CORNERS.CORNERS_0,
}: ITabProps): JSX.Element {
  const isGradientBorderColor =
    disabled && selected
      ? isGradient(borderColorSelectedDisabled)
      : selected
        ? isGradient(borderColorSelected)
        : disabled
          ? isGradient(borderColorDisabled)
          : isGradient(borderColor)

  const TabSC = isGradientBorderColor ? TabGradientBorderSC : TabSimpleBorderSC

  const TabComponent = (
    <TabSC
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      selected={selected}
      colorDefault={color}
      colorHovered={colorHovered}
      colorPressed={colorPressed}
      colorDisabled={colorDisabled}
      colorSelected={colorSelected}
      colorSelectedHovered={colorSelectedHovered}
      colorSelectedPressed={colorSelectedPressed}
      colorSelectedDisabled={colorSelectedDisabled}
      gradient={gradient}
      gradientSelected={gradientSelected}
      background={background}
      borders={borders}
      borderColor={borderColor}
      backgroundHovered={backgroundHovered}
      backgroundPressed={backgroundPressed}
      backgroundSelected={backgroundSelected}
      backgroundDisabled={backgroundDisabled}
      backgroundSelectedHovered={backgroundSelectedHovered}
      backgroundSelectedPressed={backgroundSelectedPressed}
      backgroundSelectedDisabled={backgroundSelectedDisabled}
      borderColorHovered={borderColorHovered}
      borderColorPressed={borderColorPressed}
      borderColorDisabled={borderColorDisabled}
      borderColorSelected={borderColorSelected}
      borderColorSelectedDisabled={borderColorSelectedDisabled}
      paddings={paddings}
      corners={corners}
    >
      <TextBox title={title} font={font} />
    </TabSC>
  )

  if (transitionName) {
    return (
      <AnimatedWrapper
        transitionName={transitionName}
        transitionTrigger={Boolean(selected)}
      >
        {TabComponent}
      </AnimatedWrapper>
    )
  }

  return TabComponent
}
